import { Detection, FaceDetector, FilesetResolver, ObjectDetector } from "@mediapipe/tasks-vision";
import { createElement, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { createRoot } from "react-dom/client";
import { RootState, useAppDispatch } from "store/store";
import { alertHelper } from "helpers/alert-helper";
import { AlertName } from "helpers/alert-type";
import { MediaContext } from "context/MediaContext";
import { ClientErrorTypes, SessionScope, StreamingProvider } from "globals/enums";
import { faceDetectionModelUrl, mediapipeWasmUrl, objectDetectionModelUrl, objectDetectionPersonScoreThreshold, objectDetectionScoreThreshold } from "config/constant";
import { setLinkAndVideo, setVideoThumbnail } from "store/app.slice";
import { GlobalAppState } from "globals/interfaces";
import { getSystemTime } from "helpers/common";
import { isWebGLSupported } from "helpers/face-detection";
import { handleError } from "helpers/sentry";
import { MinusIcon } from "@heroicons/react/24/outline";

const lastObjectAlertHashMap: Record<string, { timestamp: number | null; type: AlertName }> = {
  person: {
    timestamp: null,
    type: AlertName.MultiplePeopleDetected
  },
  mobile: {
    timestamp: null,
    type: AlertName.MobilePhoneDetected
  },
  book: {
    timestamp: null,
    type: AlertName.BookDetected
  }
};

const useFaceObjectDetection = () => {
  const dispatch = useAppDispatch();
  const rootRef = useRef<any>(null);

  const { videoStream, mediaStreamError } = useContext(MediaContext);

  const {
    showLinkAndVideo,
    configuration,
    isFaceDetectionEnabled,
    isObjectDetectionEnabled,
    precheckSuccess,
    session,
    alertConfigs,
    isPaused
  }: GlobalAppState = useSelector((state: RootState) => state.app);

  const [faceModel, setFaceModel] = useState<FaceDetector | null>(null);
  const personVerifierRef = useRef<ObjectDetector | null>(null);
  const objectDetectorRef = useRef<ObjectDetector | null>(null);
  const [objectDetectorLoaded, setObjectDetectorLoaded] = useState(false);
  const isMountedRef = useRef(true);

  const videoRef = useRef<HTMLVideoElement>();
  const canvasRef = useRef<HTMLCanvasElement>();
  const intervalRef = useRef<any>();

  const isCandidateAuth = session.session_type === SessionScope.CandidateAuth;
  const previousAlert = useRef<any>();
  const previousFaceDetectedTime = useRef(0);
  const previousFaceNotDetectedTime = useRef(0);
  const previousMultipleFaceDetectedTime = useRef(0);
  const noFaceDetectedCounter = useRef(0);
  const multipleFacesDetectedCounter = useRef(0);

  const init = async () => {
    if (videoStream) {
      const tvp_recorder_container = document.getElementById("tvp_recorder_container");
      const videoElement = createElement("video", {
        ref: videoRef,
        id: "recorder_video_custom",
        style: { position: "absolute" },
        width: 720,
        height: 540
      });

      const canvasElement = createElement("canvas", {
        ref: canvasRef,
        style: { position: "absolute" },
        width: 720,
        height: 540
      });

      const minimizeButtonElement = createElement("button", {
        id: "tvp_recorder_container_minimize_btn",
        children: createElement(MinusIcon),
        onClick: () => {
          dispatch(setVideoThumbnail({ showVideoThumbnail: false }));
        }
      });

      if (tvp_recorder_container && videoElement && canvasElement) {
        if (!rootRef.current) {
          rootRef.current = createRoot(tvp_recorder_container);
        }
        rootRef.current.render([videoElement, canvasElement, minimizeButtonElement]);

        if (
          !showLinkAndVideo &&
          configuration.streaming_provider !== StreamingProvider.PlatformStreaming
        ) {
          dispatch(setLinkAndVideo({ showLinkAndVideo: true }));
        }
      }
      if (isFaceDetectionEnabled || isObjectDetectionEnabled) {
        await loadModals();
      }
    }
  };

  const loadModals = async () => {
    const needsFaceModels = isFaceDetectionEnabled && (!faceModel || !personVerifierRef.current);
    const needsObjectDetector = isObjectDetectionEnabled && !objectDetectorRef.current;

    if (!needsFaceModels && !needsObjectDetector) return;

    let vision;
    try {
      vision = await FilesetResolver.forVisionTasks(mediapipeWasmUrl);
    } catch (error) {
      handleError(error, ClientErrorTypes.FaceDetectionError);
      return;
    }

    if (needsFaceModels) {
      try {
        if (!faceModel) {
          const detector = await FaceDetector.createFromOptions(vision, {
            baseOptions: {
              modelAssetPath: faceDetectionModelUrl,
              delegate: isWebGLSupported() ? "GPU" : "CPU"
            },
            runningMode: "VIDEO",
            minDetectionConfidence: 0.7,
            minSuppressionThreshold: 0.3
          });
          if (!isMountedRef.current) {
            detector.close();
            return;
          }
          setFaceModel(detector);
        }
        if (!personVerifierRef.current) {
          const verifier = await ObjectDetector.createFromOptions(vision, {
            baseOptions: {
              modelAssetPath: objectDetectionModelUrl,
              delegate: isWebGLSupported() ? "GPU" : "CPU"
            },
            runningMode: "VIDEO",
            scoreThreshold: objectDetectionPersonScoreThreshold,
            categoryAllowlist: ["person"]
          });
          if (!isMountedRef.current) {
            verifier.close();
            return;
          }
          personVerifierRef.current = verifier;
        }
      } catch (error) {
        handleError(error, ClientErrorTypes.FaceDetectionError);
      }
    }

    if (needsObjectDetector) {
      try {
        const detector = await ObjectDetector.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: objectDetectionModelUrl,
            delegate: isWebGLSupported() ? "GPU" : "CPU"
          },
          runningMode: "VIDEO",
          scoreThreshold: objectDetectionScoreThreshold,
          categoryAllowlist: ["person", "book", "cell phone", "remote"]
        });
        if (!isMountedRef.current) {
          detector.close();
          return;
        }
        objectDetectorRef.current = detector;
        setObjectDetectorLoaded(true);
      } catch (error) {
        handleError(error, ClientErrorTypes.ObjectDetectionError);
      }
    }
  };

  const handleVideoOnPlay = async () => {
    intervalRef.current = setInterval(async () => {
      try {
        if (
          canvasRef &&
          canvasRef.current &&
          videoRef.current &&
          videoRef.current.readyState === 4
        ) {
          const video: any = videoRef.current;
          const canvas = canvasRef.current;
          const ctx = canvas.getContext("2d");
          if (!ctx) return;
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          if (video && video?.width > 0 && video?.height > 0 && precheckSuccess) {
            const timestamp = Math.round(performance.now());

            if (isFaceDetectionEnabled && faceModel) {
              const facePredictions = faceModel.detectForVideo(
                videoRef.current!,
                timestamp
              ).detections;
              if (facePredictions.length > 0) {
                facePredictions.forEach((face) => {
                  if (!face.boundingBox) return;
                  ctx.beginPath();
                  ctx.strokeStyle = "green";
                  ctx.lineWidth = 4;
                  const { originX, originY, width, height } = face.boundingBox;
                  ctx.rect(originX, originY, width, height);
                  ctx.stroke();
                });
              }
              // Person verification only applies to the single-face case;
              // 0 or 2+ faces use raw face-detection counts directly.
              let verifiedPredictions = facePredictions;
              if (facePredictions.length === 1 && personVerifierRef.current) {
                const objectResult = personVerifierRef.current.detectForVideo(
                  videoRef.current!,
                  timestamp
                );
                if (objectResult.detections.length === 0) {
                  verifiedPredictions = [];
                }
              }
              if (!isPaused) {
                handleFaceDetections(verifiedPredictions);
              }
            }

            if (isObjectDetectionEnabled && objectDetectorRef.current) {
              const objectResult = objectDetectorRef.current.detectForVideo(video, timestamp);
              const detections = objectResult.detections;
              const personDetections = detections.filter(
                (d) =>
                  d.categories[0]?.categoryName === "person" &&
                  (d.categories[0]?.score ?? 0) >= objectDetectionPersonScoreThreshold
              );
              const otherDetections = detections.filter(
                (d) => d.categories[0]?.categoryName !== "person"
              );
              if (personDetections.length >= 2 || otherDetections.length > 0) {
                detections.forEach((object) => {
                  if (!object.boundingBox) return;
                  ctx.beginPath();
                  const { originX, originY, width, height } = object.boundingBox;
                  ctx.strokeStyle = "red";
                  ctx.lineWidth = 4;
                  ctx.strokeRect(originX, originY, width, height);
                  ctx.fillStyle = "red";
                });

                if (!isPaused) {
                  checkMobileAlert(detections);
                  checkBookAlert(detections);
                  checkPersonAlert(personDetections);
                }
              }
            }
          }
        }
      } catch (error) {
        handleError(error, ClientErrorTypes.FaceDetectionError);
      }
    }, 500);
  };

  const MAX_NO_FACE_DETECTED_FRAMES = 15;
  const MAX_MULTIPLE_FACE_DETECTED_FRAMES = 10;

  const handleFaceDetections = (facePredictions: any[]) => {
    const faceCount = facePredictions.length;
    let faceAlertType: AlertName | undefined;
    if (faceCount === 0) {
      noFaceDetectedCounter.current++;
      if (noFaceDetectedCounter.current >= MAX_NO_FACE_DETECTED_FRAMES) {
        faceAlertType = AlertName.FaceNotDetected;
        noFaceDetectedCounter.current = 0;
        multipleFacesDetectedCounter.current = 0;
      }
    } else if (faceCount === 1) {
      noFaceDetectedCounter.current = 0;
      multipleFacesDetectedCounter.current = 0;
      faceAlertType = AlertName.FaceDetected;
    } else if (facePredictions.length > 1) {
      multipleFacesDetectedCounter.current++;
      if (multipleFacesDetectedCounter.current >= MAX_MULTIPLE_FACE_DETECTED_FRAMES) {
        faceAlertType = AlertName.MultipleFaceDetected;
        multipleFacesDetectedCounter.current = 0;
        noFaceDetectedCounter.current = 0;
      }
    }

    if (
      !faceAlertType ||
      (previousAlert.current === AlertName.FaceDetected && previousAlert.current === faceAlertType)
    ) {
      return;
    }

    switch (faceAlertType) {
      case AlertName.FaceDetected:
        if (
          !previousFaceDetectedTime.current ||
          previousFaceNotDetectedTime.current > previousFaceDetectedTime.current
        ) {
          handleFaceAlert(faceAlertType, previousFaceDetectedTime, facePredictions);
        }
        break;
      case AlertName.FaceNotDetected:
        handleFaceAlert(faceAlertType, previousFaceNotDetectedTime, facePredictions);
        break;
      case AlertName.MultipleFaceDetected:
        handleFaceAlert(faceAlertType, previousMultipleFaceDetectedTime, facePredictions);
        break;
      default:
        return;
    }
  };

  const handleFaceAlert = (
    faceAlertType: AlertName,
    previousTime: React.MutableRefObject<number>,
    facePredictions: any[]
  ) => {
    previousAlert.current = faceAlertType;
    previousTime.current = getSystemTime().getTime();
    alertHelper(alertConfigs).raiseAlert(
      faceAlertType,
      dispatch,
      "",
      JSON.stringify({ model_predictions: facePredictions })
    );
  };

  const checkPersonAlert = (personDetections: Detection[]) => {
    if (personDetections.length > 1) {
      if (
        !lastObjectAlertHashMap.person ||
        getSystemTime().getTime() - lastObjectAlertHashMap.person.timestamp > 10000
      ) {
        alertHelper(alertConfigs).raiseAlert(
          lastObjectAlertHashMap.person.type,
          dispatch,
          "",
          JSON.stringify({
            detection_confidence: personDetections[0].categories[0]?.score,
            model_predictions: personDetections
          })
        );
        lastObjectAlertHashMap.person.timestamp = getSystemTime().getTime();
      }
    }
  };

  const checkMobileAlert = (objectPredictions: Detection[]) => {
    const mobiles = objectPredictions.filter(
      (pred) =>
        pred.categories[0]?.categoryName === "cell phone" ||
        pred.categories[0]?.categoryName === "remote"
    );
    if (mobiles.length > 0) {
      if (
        !lastObjectAlertHashMap.mobile.timestamp ||
        getSystemTime().getTime() - lastObjectAlertHashMap.mobile.timestamp > 10000
      ) {
        alertHelper(alertConfigs).raiseAlert(
          lastObjectAlertHashMap.mobile.type,
          dispatch,
          "",
          JSON.stringify({
            detection_confidence: mobiles[0].categories[0]?.score,
            model_predictions: mobiles
          })
        );
        lastObjectAlertHashMap.mobile.timestamp = getSystemTime().getTime();
      }
    }
  };

  const checkBookAlert = (objectPredictions: Detection[]) => {
    const books = objectPredictions.filter(
      (pred) => pred.categories[0]?.categoryName === "book"
    );
    if (books.length > 0) {
      if (
        !lastObjectAlertHashMap.book.timestamp ||
        getSystemTime().getTime() - lastObjectAlertHashMap.book.timestamp > 10000
      ) {
        alertHelper(alertConfigs).raiseAlert(
          lastObjectAlertHashMap.book.type,
          dispatch,
          "",
          JSON.stringify({
            detection_confidence: books[0].categories[0]?.score,
            model_predictions: books
          })
        );
        lastObjectAlertHashMap.book.timestamp = getSystemTime().getTime();
      }
    }
  };

  useEffect(() => {
    const isModelLoaded = faceModel || objectDetectorLoaded;
    if (isModelLoaded && !mediaStreamError && precheckSuccess) {
      clearInterval(intervalRef.current);
      handleVideoOnPlay();
    }
    return () => {
      clearInterval(intervalRef.current);
    };
  }, [faceModel, objectDetectorLoaded, mediaStreamError, precheckSuccess, isPaused]);

  useEffect(() => {
    if (
      mediaStreamError &&
      (mediaStreamError.name === "AudioHardwareFailedError" ||
        mediaStreamError.name === "VideoHardwareFailedError")
    ) {
      clearInterval(intervalRef.current);
    }
  }, [mediaStreamError]);

  useEffect(() => {
    const video: any = videoRef.current;
    if (video && videoStream?.id) {
      video.srcObject = videoStream;
      video.setAttribute("playsInline", "");
      video.setAttribute("webkit-playsinline", "true");

      // hotfix for letting the new videostream update
      // after being stopped and started at different pages
      setTimeout(() => {
        video.play();
      }, 150);
    }
  }, [videoRef.current, videoStream]);

  useEffect(() => {
    if (videoStream && !isCandidateAuth) {
      if (configuration.streaming_provider !== StreamingProvider.PlatformStreaming) {
        init();
      } else if (precheckSuccess) {
        init();
      }
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  }, [
    videoStream,
    isFaceDetectionEnabled,
    isObjectDetectionEnabled,
    showLinkAndVideo,
    precheckSuccess
  ]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      personVerifierRef.current?.close();
      personVerifierRef.current = null;
      objectDetectorRef.current?.close();
      objectDetectorRef.current = null;
    };
  }, []);

  return [isFaceDetectionEnabled, !!isObjectDetectionEnabled];
};

export default useFaceObjectDetection;
